import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import "./Project.scss";
import WelcomeScreen from "./components/WelcomeScreen/WelcomeScreen";
import ProjectOverview from "./components/ProjectOverview/ProjectOverview";
import ResponsiveDesign from "./components/ResponsiveDesign/ResponsiveDesign";
import ProjectFilesWithChat from "./components/ProjectFilesWithChat/ProjectFilesWithChat";
import { generateIntroData, getAllProjects, getPresignedUrl } from "../../apiservice/Chatservice";
import ContentWrapper from "../../components/ContentWrapper/ContentWrapper";
import ContentHeader from "../../components/ContentHeader/ContentHeader";
import { Icons } from "../../assets/icons/icon";
import Content from "../../components/Content/Content";
import { CustomModal } from "../../components/CustomModal/CustomModal";
import NewprojectForm, { NewProject, NewProjectFormData } from "../../components/NewProjectForm/NewProjectForm";
import { SubmitHandler } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import ProjectCard from "./components/ProjectCard/ProjectCard";
import { showToast } from "../../components/Toaster/Toaster";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

export interface ProjectData {
    p_id: string;
    p_name: string;
    p_description: string;
    p_url: string;
    p_image_url: string | null;
    p_token: string | null;
    p_main_branch: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    llm_intro_status: null | 'success' | 'failed' | 'inProgress';
}

const Project: FunctionComponent = () => {
    const [loading, setLoading] = useState(false);
    const [wsLoading, setWSLoading] = useState(false);
    const [newProjectLoading, setNewProjectLoading] = useState(false);
    const [projectsData, setProjectsData] = useState<ProjectData[]>([]);
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
    const currentStep = useSelector((state: RootState) => state.projectDetails.currentStep);
    const selectedProject = useSelector((state: RootState) => state.projectDetails.selectedProject)

    useEffect(() => {
        getAllAvailableProjects();
    }, []);

    const getAllAvailableProjects = async () => {
        setLoading(true);
        const data = await getAllProjects();
        setProjectsData(data);
        setLoading(false);
    }

    const handleCreateNewProject = () => {
        setShowCreateProjectModal(true);
    }

    const handleCloseCreateNewProject = () => {
        setShowCreateProjectModal(false);
    }

    const handleSubmit: SubmitHandler<NewProjectFormData> = async (data) => {
        setNewProjectLoading(true);
        const payload: NewProject = {
            projectName: data.projectName,
            description: data.description,
            gitHubLink: data.gitHubLink,
            branchName: data.branchName,
            thumbnailImage: data.thumbnailImage ?? undefined,
            bitToken: data.bitToken ?? undefined,
        };

        if (payload.thumbnailImage) {
            // Get the presigned URL
            const presigned = await getPresignedUrl(payload.thumbnailImage, data.projectName);
            console.log("Presigned URL Response:", presigned);
            if (presigned?.url) {
                // Upload the image to S3 using the presigned URL
                try {
                    await fetch(presigned.url, {
                        method: 'PUT',
                        body: data.thumbnailImage,
                        headers: {
                            'Content-Type': payload.thumbnailImage.type || 'application/octet-stream',
                        },
                    });
                } catch (err) {
                    showToast("Failed to upload project card image", "error");
                }
            }
        }
        const response = await generateIntroData(payload);
        if (response) {
            await getAllAvailableProjects();
            handleCloseCreateNewProject();
            showToast(response?.message ?? "Project created successfully.", "success");
        }
        setNewProjectLoading(false);
    }

    // Stepper components for steps 1-4
    const stepComponents = [
        <WelcomeScreen key={1} setLoading={setWSLoading} />,
        <ProjectOverview key={2} loading={wsLoading} />,
        <ResponsiveDesign key={3} loading={wsLoading} />,
        <ProjectFilesWithChat key={4} />,
    ];

    const showStepper = selectedProject && currentStep >= 1 && currentStep <= 4;
    const getRenderingContent = () => {
        if (showStepper) {
            return stepComponents[currentStep - 1];
        } else if (loading) {
            return <LoadingSpinner />;
        } else if (projectsData?.length === 0) {
            return <div className="no-project">No projects found</div>;
        } else {
            return (
                <div className="projects-card-wrapper">
                    {projectsData?.map?.((project: ProjectData) => <ProjectCard key={project.p_id} project={project} />)}
                </div>
            );
        }
    }

    return (
        showStepper ? (
            stepComponents[currentStep - 1]
        ) : (
            <ContentWrapper>
                <Fragment>
                    <ContentHeader
                        title={"Projects"}
                        subTitle="Manage and track your projects"
                        action={{
                            label: "New Project",
                            onClick: handleCreateNewProject,
                            icon: <img src={Icons.Plus} alt="New" />,
                            height: "48px"
                        }}
                    />
                    <Content isFullHeight>
                        <Fragment>
                            {getRenderingContent()}
                        </Fragment>
                    </Content>
                    {
                        showCreateProjectModal && (
                            <CustomModal
                                open={showCreateProjectModal}
                                handleClose={handleCloseCreateNewProject}
                            >
                                <NewprojectForm handleClose={handleCloseCreateNewProject} handleSubmit={handleSubmit} isPending={newProjectLoading} />
                            </CustomModal>
                        )
                    }
                </Fragment>
            </ContentWrapper>
        )

    )
}

export default Project;